import { useUnblockCreators } from "@/api/blocklist";
import { Button } from "@/components/base/buttons/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { error, success } from "@/providers/ToastProvider";

interface BulkUnblockCreatorsModalProps {
  creatorOpenIds: string[];
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: () => void;
}

export function BulkUnblockCreatorsModal({
  creatorOpenIds,
  open,
  onOpenChange,
  onSuccess,
}: BulkUnblockCreatorsModalProps) {
  const unblockCreators = useUnblockCreators();
  const busy = unblockCreators.isPending;
  const count = creatorOpenIds.length;

  const handleUnblock = async () => {
    try {
      await unblockCreators.mutateAsync({ creatorOpenIds, withExemption: false });
      success(`${count} creator${count === 1 ? "" : "s"} unblocked successfully.`);
      onOpenChange(false);
      onSuccess?.();
    } catch {
      error("Failed to unblock creators");
    }
  };

  const handleExempt = async () => {
    try {
      await unblockCreators.mutateAsync({ creatorOpenIds, withExemption: true });
      success(`${count} creator${count === 1 ? "" : "s"} unblocked and exempted from rules successfully.`);
      onOpenChange(false);
      onSuccess?.();
    } catch {
      error("Failed to unblock creators");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader className="pt-2">
          <DialogTitle className="mb-3">
            Unblock {count} creator{count === 1 ? "" : "s"}?
          </DialogTitle>
          <DialogDescription className="mb-5">
            They'll appear in your recommendations, search, and imports again. A rule may re-block them on your next
            import — choose 'Unblock and exempt from rules' to keep them permanently exempt.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter className="flex-row items-center justify-between gap-2 sm:justify-between">
          <Button color="secondary" size="md" isDisabled={busy} onClick={() => onOpenChange(false)}>
            Close
          </Button>
          <div className="flex gap-2">
            <Button color="secondary" size="md" isDisabled={busy} onClick={handleUnblock}>
              Unblock
            </Button>
            <Button color="primary" size="md" isDisabled={busy} onClick={handleExempt}>
              Unblock and exempt from rules
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
